import React from "react";
import { ChevronRight } from "lucide-react";
import { MenuItem } from "./types";

interface SidebarItemProps {
  item: MenuItem;
  isCollapsed: boolean;
  openSubmenu: string | null;
  openChildSubmenu: string | null;
  isActive: boolean;
  isSubItemActive: (parentId: string, label: string) => boolean;
  isChildItemActive: (
    parentId: string,
    subParentId: string,
    label: string
  ) => boolean;
  onToggleSubmenu: (id: string) => void;
  onToggleChildSubmenu: (id: string) => void;
  onPrimaryClick: (item: MenuItem, hasRealSubmenu: boolean) => void;
  onSubLabelClick: (sectionId: string, label: string) => void;
  onChildLabelClick: (
    sectionId: string,
    parentId: string,
    label: string
  ) => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  isCollapsed,
  openSubmenu,
  openChildSubmenu,
  isActive,
  isSubItemActive,
  isChildItemActive,
  onToggleSubmenu,
  onToggleChildSubmenu,
  onPrimaryClick,
  onSubLabelClick,
  onChildLabelClick,
}) => {
  const hasRealSubmenu =
    Array.isArray(item.subItems) && item.subItems.length > 0;

  return (
    <li key={item.id}>
      <button
        onClick={() => onPrimaryClick(item, hasRealSubmenu)}
        className={`w-full flex items-center transition-colors text-[rgb(51,122,183)] hover:bg-gray-100 ${
          isCollapsed ? "px-4 py-2" : "pl-4 pr-3 py-2"
        } ${openSubmenu === item.id ? "bg-gray-50" : ""} ${
          !hasRealSubmenu ? "border-b border-gray-300 last:border-b-0" : ""
        } ${
          isActive
            ? "bg-[#0087c1]/10 border-l-4 border-l-[#0087c1] text-[#0087c1]"
            : ""
        }`}
      >
        <div className={`flex items-center w-full px-2`}>
          <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
            <item.icon
              className={`h-4 w-4 ${
                isActive ? "text-[#0087c1]" : "text-[rgb(51,122,183)]"
              }`}
            />
          </div>
          <div
            className={`flex items-center justify-between flex-1 ml-2 transition-all duration-300 ${
              isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
            }`}
          >
            <span className="text-sm font-normal whitespace-nowrap overflow-hidden text-ellipsis">
              {item.label}
            </span>
            {hasRealSubmenu && (
              <ChevronRight
                className={`h-4 w-4 text-[rgb(51,122,183)] flex-shrink-0 ml-1 transition-transform duration-300 ease-in-out ${
                  openSubmenu === item.id ? "rotate-90" : ""
                }`}
              />
            )}
          </div>
        </div>
      </button>

      {!isCollapsed && hasRealSubmenu && (
        <div
          className={`overflow-hidden transform-gpu origin-top transition-all duration-300 ease-out ${
            openSubmenu === item.id
              ? "max-h-96 opacity-100 scale-y-100"
              : "max-h-0 opacity-0 scale-y-95"
          }`}
        >
          <ul
            className={`${
              openSubmenu === item.id ? "bg-gray-100" : "bg-gray-50"
            }`}
          >
            {(item.subItems || []).map((subItem, subIndex) => {
              if (typeof subItem === "string") {
                const isSubActive = isSubItemActive(item.id, subItem);
                return (
                  <li key={`${item.id}-s-${subIndex}`}>
                    <button
                      onClick={() => onSubLabelClick(item.id, subItem)}
                      className={`w-full flex items-center justify-start px-8 py-2 text-sm text-[rgb(51,122,183)] hover:bg-gray-100 transition-colors ${
                        isSubActive
                          ? "bg-[#0087c1]/10 border-l-4 border-l-[#0087c1] text-[#0087c1] font-medium"
                          : ""
                      }`}
                    >
                      <span className="flex-1 text-left whitespace-normal break-words">
                        {subItem}
                      </span>
                    </button>
                  </li>
                );
              }

              return (
                <li key={subItem.id}>
                  <button
                    onClick={() =>
                      subItem.hasSubmenu && onToggleChildSubmenu(subItem.id)
                    }
                    className="w-full flex items-center justify-between px-8 py-2 text-sm text-[rgb(51,122,183)] hover:bg-gray-100 transition-colors"
                  >
                    <span>{subItem.label}</span>
                    {subItem.hasSubmenu && (
                      <ChevronRight
                        className={`h-4 w-4 ml-2 transition-transform duration-300 ease-in-out ${
                          openChildSubmenu === subItem.id ? "rotate-90" : ""
                        }`}
                      />
                    )}
                  </button>

                  {subItem.hasSubmenu && subItem.subItems && (
                    <div
                      className={`overflow-hidden transform-gpu origin-top transition-all duration-300 ease-out ${
                        openChildSubmenu === subItem.id
                          ? "max-h-60 opacity-100 scale-y-100"
                          : "max-h-0 opacity-0 scale-y-95"
                      }`}
                    >
                      <ul className="bg-gray-200  ">
                        {subItem.subItems.map((child, childIndex) => {
                          const isChildActive = isChildItemActive(
                            item.id,
                            subItem.id,
                            child
                          );
                          return (
                            <li key={`${subItem.id}-c-${childIndex}`}>
                              <button
                                onClick={() =>
                                  onChildLabelClick(item.id, subItem.id, child)
                                }
                                className={`w-full flex items-center px-12 py-2 text-sm text-[rgb(51,122,183)] hover:bg-gray-300 transition-colors ${
                                  isChildActive
                                    ? "bg-[#0087c1]/10 border-l-4 border-l-[#0087c1] text-[#0087c1] font-medium"
                                    : ""
                                }`}
                              >
                                <span>{child}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </li>
  );
};

export default SidebarItem;
